'use client';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarIcon } from '@radix-ui/react-icons';
import { DateRange } from 'react-day-picker';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Button } from './Button';
import {
  cn,
  dateTimeFormatWithoutSeconds,
  formatDateTime,
  utcDateRangeToZoned,
  zonedDateRangeToUtc,
} from '@ais-ui/utils';
import { Calendar } from './Calendar';
import { Input } from './Input';
import { Label } from './Label';
import { Separator } from './Separator';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { clampTimeValue } from '../../../../apps/ais/src/modules/utils/clampTimeValue';

// Define the props for the component
interface DatePickerWithRangeProps {
  date: DateRange | undefined;
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  timeZone: string;
  dateTimeFormat: string;
  dateLimit: 'past' | 'future' | 'all';
  disabled?: boolean;
  startDatetimeThreshold?: Date;
  endDatetimeThreshold?: Date;
  className?: string;
}

const setTimeToNow = (localDate: Date | undefined, timeZone: string) => {
  if (!localDate) {
    return;
  }

  const now = new Date();
  const zonedNow = utcToZonedTime(now, timeZone);

  localDate.setTime(zonedNow.getTime());
};

const setTimeToMidday = (localDate: Date | undefined) => {
  if (!localDate) {
    return;
  }

  // Set the time to midday (12:00 PM) in local time
  localDate.setHours(12, 0, 0, 0);
};

const setTimeToPastMidday = (localDate: Date | undefined) => {
  if (!localDate) {
    return;
  }

  // Set the time to midday (12:00 PM) in local time
  localDate.setHours(13, 0, 0, 0);
};

const isFutureDatetime = (localDate: Date | undefined, timeZone: string) => {
  if (!localDate) {
    return false;
  }
  const dateInUtc = zonedTimeToUtc(localDate, timeZone);
  const now = new Date();
  return dateInUtc > now;
};

const isPastDatetime = (localDate: Date | undefined, timeZone: string) => {
  if (!localDate) {
    return false;
  }

  const dateInUtc = zonedTimeToUtc(localDate, timeZone);
  const now = new Date();

  return dateInUtc < now;
};

const isToday = (localDate: Date | undefined, timeZone: string) => {
  if (!localDate) {
    return false;
  }

  // Convert the current UTC date to the given timezone's local date
  const nowInTimeZone = utcToZonedTime(new Date(), timeZone);

  // Extract the date parts for comparison
  const year = nowInTimeZone.getFullYear();
  const month = nowInTimeZone.getMonth();
  const day = nowInTimeZone.getDate();

  // Convert the localDate to the given timezone's local date
  const dateInTimeZone = utcToZonedTime(localDate, timeZone);

  // Compare if the year, month, and date match
  return (
    dateInTimeZone.getFullYear() === year &&
    dateInTimeZone.getMonth() === month &&
    dateInTimeZone.getDate() === day
  );
};

export const DateTimePickerWithRange = ({
  className,
  date,
  setDate,
  timeZone = 'UTC',
  dateTimeFormat,
  dateLimit = 'all',
  disabled = false,
  startDatetimeThreshold,
  endDatetimeThreshold,
}: DatePickerWithRangeProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const localDate = useMemo(
    () => utcDateRangeToZoned(date, timeZone),
    [date, timeZone, open]
  );

  const [defaultMonth, setDefaultMonth] = useState<Date | undefined>(
    localDate?.from
  );

  useEffect(() => {
    if (open && localDate?.from) {
      setDefaultMonth(localDate.from);
    }
  }, [open, localDate]);

  const setUtcDateRange = useCallback(
    (dateRange: DateRange | undefined) => {
      setDate(zonedDateRangeToUtc(dateRange, timeZone));
    },
    [setDate, timeZone]
  );

  const handleDateSelection = useCallback(
    (selectedDate: DateRange | undefined) => {
      const selectedToDate = selectedDate?.to;
      const selectedFromDate = selectedDate?.from;

      // Set the time to what it was before as this is only date selection that sets time to 00:00
      if (selectedFromDate && date?.from) {
        const zonedDate = utcToZonedTime(date.from, timeZone);
        selectedFromDate.setHours(zonedDate.getHours(), zonedDate.getMinutes());
      }

      if (selectedToDate && date?.to) {
        const zonedDate = utcToZonedTime(date.to, timeZone);
        selectedToDate.setHours(zonedDate.getHours(), zonedDate.getMinutes());
      }

      if (dateLimit === 'past') {
        // Make sure past times don't go into the future
        if (isFutureDatetime(selectedToDate, timeZone)) {
          setTimeToNow(selectedToDate, timeZone);
        }

        if (!date?.to && isToday(selectedToDate, timeZone)) {
          setTimeToNow(selectedToDate, timeZone);
        }

        if (
          !date?.to &&
          isPastDatetime(selectedToDate, timeZone) &&
          !isToday(selectedToDate, timeZone)
        ) {
          setTimeToPastMidday(selectedToDate);
        }

        if (!date?.from && isPastDatetime(selectedFromDate, timeZone)) {
          setTimeToMidday(selectedFromDate);
        }
      } else if (dateLimit === 'future') {
        if (isPastDatetime(selectedFromDate, timeZone)) {
          setTimeToNow(selectedFromDate, timeZone);
        }

        if (!date?.from && isToday(selectedFromDate, timeZone)) {
          setTimeToNow(selectedFromDate, timeZone);
        }

        if (
          !date?.to &&
          isFutureDatetime(selectedToDate, timeZone) &&
          !isToday(selectedToDate, timeZone)
        ) {
          setTimeToPastMidday(selectedToDate);
        }

        if (!date?.from && isFutureDatetime(selectedFromDate, timeZone)) {
          setTimeToMidday(selectedFromDate);
        }
      }

      // Make sure to is always after from
      if (
        selectedFromDate &&
        selectedToDate &&
        selectedFromDate > selectedToDate
      ) {
        selectedFromDate.setHours(
          selectedToDate.getHours(),
          selectedToDate.getMinutes()
        );
      }

      setUtcDateRange(selectedDate);
    },
    [date, timeZone, setUtcDateRange, dateLimit]
  );

  const onSelect = () => {
    setOpen(false);
  };

  const disableDate = useCallback(
    (zonedDateToCheck: Date) => {
      const today = new Date();
      const zonedToday = utcToZonedTime(today, timeZone);
      zonedToday.setHours(0, 0, 0, 0);

      if (startDatetimeThreshold && zonedDateToCheck < startDatetimeThreshold) {
        return true;
      }
      if (endDatetimeThreshold && zonedDateToCheck > endDatetimeThreshold) {
        return true;
      }

      switch (dateLimit) {
        case 'past':
          return zonedDateToCheck > zonedToday;
        case 'future':
          return zonedDateToCheck < zonedToday;
        case 'all':
        default:
          return false;
      }
    },
    [timeZone, startDatetimeThreshold, endDatetimeThreshold, dateLimit]
  );

  const formattedDate = useMemo(() => {
    if (!localDate) {
      return 'Pick a date and time';
    }

    if (localDate.from && localDate.to) {
      const formattedFromDate = formatDateTime(
        localDate.from,
        dateTimeFormatWithoutSeconds(dateTimeFormat)
      );
      const formattedToDate = formatDateTime(
        localDate.to,
        dateTimeFormatWithoutSeconds(dateTimeFormat)
      );
      return `${formattedFromDate} - ${formattedToDate}`;
    } else if (localDate.from) {
      const formattedFromDate = formatDateTime(
        localDate.from,
        dateTimeFormatWithoutSeconds(dateTimeFormat)
      );
      return formattedFromDate;
    } else {
      return 'Pick a date and time';
    }
  }, [localDate, dateTimeFormat]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const fromYear = useMemo(() => currentYear - 5, [currentYear]);
  const toYear = useMemo(() => currentYear + 2, [currentYear]);

  const onChangeFrom = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: 'minutes' | 'hours') => {
      const value = e.target.value.trim();
      if (!value) {
        // If no value is provided, set the whole date range to undefined
        setDate(undefined);
      } else if (!localDate || !localDate.from) {
        setDate(undefined);
      } else {
        const numberValue = clampTimeValue(parseInt(value, 10), type);

        const updatedLocalDate = new Date(localDate.from);

        if (type === 'minutes') {
          updatedLocalDate.setMinutes(numberValue);
        } else if (type === 'hours') {
          updatedLocalDate.setHours(numberValue);
        }

        setDate((prevDate) => {
          if (!prevDate) {
            return undefined;
          }

          const updatedUtcDate = zonedTimeToUtc(updatedLocalDate, timeZone);

          // Makes sure to update to, if it's before from
          if (prevDate.to && prevDate.to < updatedUtcDate) {
            return {
              from: updatedUtcDate,
              to: updatedUtcDate,
            };
          }

          return {
            ...prevDate,
            from: zonedTimeToUtc(updatedLocalDate, timeZone),
          };
        });
      }
    },
    [localDate, setDate, timeZone]
  );

  const onChangeTo = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: 'minutes' | 'hours') => {
      const value = e.target.value.trim();
      if (!value) {
        setDate((prevDate) => {
          if (!prevDate) {
            return undefined;
          }
          return {
            ...prevDate,
            to: undefined,
          };
        });
      } else if (!localDate || !localDate.to) {
        setDate((prevDate) => {
          if (!prevDate) {
            return undefined;
          }
          return {
            ...prevDate,
            to: undefined,
          };
        });
      } else {
        const numberValue = clampTimeValue(parseInt(value, 10), type);

        const updatedLocalDate = new Date(localDate.to);

        if (type === 'minutes') {
          updatedLocalDate.setMinutes(numberValue);
        } else if (type === 'hours') {
          updatedLocalDate.setHours(numberValue);
        }

        setDate((prevDate) => {
          if (!prevDate) {
            return undefined;
          }

          const updatedUtcDate = zonedTimeToUtc(updatedLocalDate, timeZone);

          // Makes sure to update from, if it's after to
          if (prevDate.from && prevDate.from > updatedUtcDate) {
            return {
              from: updatedUtcDate,
              to: updatedUtcDate,
            };
          }

          return {
            ...prevDate,
            to: updatedUtcDate,
          };
        });
      }
    },
    [localDate, setDate, timeZone]
  );

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal',
              !localDate && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formattedDate}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 flex flex-col" align="start">
          <Calendar
            disabled={(localDate) => disableDate(localDate)}
            initialFocus
            mode="range"
            captionLayout="dropdown-buttons"
            defaultMonth={defaultMonth}
            selected={localDate}
            onSelect={handleDateSelection}
            numberOfMonths={2}
            fromYear={fromYear}
            toYear={toYear}
          />
          <Separator></Separator>

          <div className="grid grid-cols-11">
            <div className="col-start-1 col-span-5 p-4">
              <div className="flex justify-between items-center">
                <Input
                  value={
                    localDate?.from
                      ? String(localDate.from.getHours()).padStart(2, '0')
                      : ''
                  }
                  id="from-hours"
                  placeholder="HH"
                  type="number"
                  onChange={(e) => onChangeFrom(e, 'hours')}
                  className="h-8 w-[70px]"
                ></Input>
                <Label> : </Label>
                <Input
                  value={
                    localDate?.from
                      ? String(localDate.from.getMinutes()).padStart(2, '0')
                      : ''
                  }
                  id="from-minutes"
                  placeholder="MM"
                  type="number"
                  onChange={(e) => onChangeFrom(e, 'minutes')}
                  className="h-8 w-[70px]"
                ></Input>
              </div>
            </div>
            <div className="flex col-span-1 justify-center">
              <Separator
                className="h-full col-span-1"
                orientation="vertical"
              ></Separator>
            </div>

            <div className="space-x-4 col-start-7 col-span-5 p-4">
              <div className="flex justify-between items-center">
                <Input
                  value={
                    localDate?.to
                      ? String(localDate.to.getHours()).padStart(2, '0')
                      : ''
                  }
                  id="to-hours"
                  placeholder="HH"
                  type="number"
                  onChange={(e) => onChangeTo(e, 'hours')}
                  className="h-8 w-[70px]"
                ></Input>

                <Label> : </Label>

                <Input
                  value={
                    localDate?.to
                      ? String(localDate.to.getMinutes()).padStart(2, '0')
                      : ''
                  }
                  id="to-minutes"
                  placeholder="MM"
                  type="number"
                  onChange={(e) => onChangeTo(e, 'minutes')}
                  className="h-8 w-[70px]"
                ></Input>
              </div>
            </div>
          </div>
          <Separator></Separator>
          <div className="flex p-4 justify-between items-baseline">
            <div className="space-x-2">
              <Label className="bold text-primary">Selected:</Label>
              <Label className="text-muted-foreground">{formattedDate}</Label>
            </div>
            <Button id="datetimerange-select" onClick={onSelect} type="submit">
              Select
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
